import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';

import ButtonCallAction from '@/components/BI/buttonCallAction';
import SectionTitle from '@/components/atoms/fonts/SectionTitle';
import useSnackbar from '@/hooks/useSnackbar';
import { ReactComponent as PDFIcon } from '@assets/icons/PDFIcon.svg';
import { ReactComponent as RelatorioFechamentoIcon } from '@assets/icons/RelatorioFechamento.svg';
import useReportService from '@services/report';

import PageHeader from './components/PageHeader';
import { PageContent, TitleWrapper } from './style';
import Accesses from './tabs/Accesses';
import { ActionPlans } from './tabs/ActionPlans';
import { CheckAgro } from './tabs/CheckAgro';
import Courses from './tabs/Courses';
import Goals from './tabs/Goals';
import Performances from './tabs/Performances';
import Properties from './tabs/Properties';
import Reports from './tabs/Reports';
import ReportsDashboard from './tabs/ReportsDashboard';
import Users from './tabs/Users';
import Visits from './tabs/Visits';

const EnterprisePage = ({ idEnterprise, segmentId }) => {
    const params = useParams();
    const currentUserRole = Number(sessionStorage.getItem('currentUserRole'));
    const segment = useSelector((state) => state.auth.currentSegment);
    const { isLoadingReport } = useSelector((state) => state);
    const [isLoadingMonthlyReport, setIsLoadingMonthlyReport] = useState(false);
    const dispatch = useDispatch();
    const reportService = useReportService();
    const snackbar = useSnackbar();
    const tabInitial = currentUserRole === 5 ? 'users' : 'engagement';
    const tab = params.tab ?? tabInitial;

    const handleButtonClick = () => {
        dispatch({ type: 'MANAGER_PANEL_REPORT_BUTTON_CLICK', payload: true });
    };

    const downloadLatestMonthlyReport = async () => {
        setIsLoadingMonthlyReport(true);
        try {
            const response = await reportService.downloadMonthlyClosingReports(idEnterprise);
            const contentDisposition = response.headers['content-disposition'];
            let filename = `relatorios-fechamento-mensal.zip`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
                if (match?.[1]) {
                    filename = decodeURIComponent(match[1]);
                }
            }
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao solicitar relatório mensal:', error);

            let errorMessage = 'Erro ao solicitar relatório mensal. Tente novamente.';
            try {
                if (error.response?.data instanceof Blob) {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    if (json.error) errorMessage = json.error;
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                }
            } catch (_) {}

            snackbar(errorMessage, 'error');
        } finally {
            setIsLoadingMonthlyReport(false);
        }
    };

    const renderPageContent = () => {
        switch (tab) {
            case 'engagement':
                return <Accesses idEnterprise={idEnterprise} tab={tab} />;
            case 'users':
                return <Users idEnterprise={idEnterprise} segmentId={segmentId} tab={tab} />;
            case 'properties':
                return <Properties idEnterprise={idEnterprise} tab={tab} />;
            // case 'visits':
            //     return <Visits idEnterprise={idEnterprise} tab={tab} />;
            case 'reports':
                return <ReportsDashboard idEnterprise={idEnterprise} />;
            //   return <Reports idEnterprise={idEnterprise} tab={tab} />;
            case 'goals':
                return <Goals idEnterprise={idEnterprise} tab={tab} currentUserRole={currentUserRole} />;
            case 'action_plan':
                return <ActionPlans idEnterprise={idEnterprise} tab={tab} />;
            case 'performance':
                return <Performances idEnterprise={idEnterprise} tab={tab} />;
            case 'check_agro':
                return <CheckAgro idEnterprise={idEnterprise} tab={tab} />;
            case 'courses':
                return <Courses idEnterprise={idEnterprise} tab={tab} />;
            default:
                // return <h2>Página &apos;{params.tab}&apos; em desenvolvimento!</h2>;
                return <Users idEnterprise={idEnterprise} segmentId={segmentId} tab={tab} />;
        }
    };

    return (
        <>
            <TitleWrapper>
                <SectionTitle tituloString={'Painel do Gestor'} marginHorizontal={'0'} />
                {segment == 1 && (
                    <div style={{ display: 'flex', gap: '12px' }}>
                        {currentUserRole === 4 && (
                            <ButtonCallAction
                                sx={{ fontSize: 16, fontWeight: 700, padding: '16px' }}
                                onClick={downloadLatestMonthlyReport}
                                loading={isLoadingMonthlyReport}
                                Icon={RelatorioFechamentoIcon}
                            >
                                Fechamento Mês
                            </ButtonCallAction>
                        )}
                        {!['engagement', 'visits', 'courses', 'reports', 'goals', 'performance'].includes(tab) && (
                            <ButtonCallAction
                                sx={{ fontSize: 16, fontWeight: 700, padding: '16px' }}
                                onClick={handleButtonClick}
                                loading={isLoadingReport}
                                Icon={PDFIcon}
                            >
                                Gerar Relatório
                            </ButtonCallAction>
                        )}
                    </div>
                )}
            </TitleWrapper>
            <Grid container alignItems={'center'} justifyContent={'space-between'} marginBottom={'2rem'}>
                <Grid item xs={12}>
                    {segmentId ? <PageHeader idEnterprise={idEnterprise} /> : null}
                </Grid>
            </Grid>

            <PageContent>{renderPageContent()}</PageContent>
        </>
    );
};

export default EnterprisePage;
